import React, { useState } from 'react';
import { Scan, Loader } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../components/common/FeedbackSystem';
import { whatCmsService } from '../../../lib/whatCmsService';
import { connectivityService } from '../../../lib/connectivityService';
import { callEdge } from '../../../lib/apiClient';

interface BulkScanButtonProps {
  onScanComplete?: () => void;
  maxConcurrent?: number;
}

/**
 * BulkScanButton - Bouton permettant de lancer une analyse rapide sur tous les prospects
 */
const BulkScanButton: React.FC<BulkScanButtonProps> = ({ 
  onScanComplete, 
  maxConcurrent = 3 // Nombre maximum de prospects analysés en parallèle
}) => {
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [processedCount, setProcessedCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { showSuccess, showError } = useToast();

  // Fonction pour normaliser l'URL
  const normalizeUrl = (url: string): string => {
    if (!url) return '';
    
    let formattedUrl = url.trim();
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }
    
    return formattedUrl;
  };

  // Extraire le domaine à partir de l'URL
  const extractDomain = (url: string): string => {
    if (!url) return '';
    
    try {
      const urlObj = new URL(normalizeUrl(url));
      const hostname = urlObj.hostname;
      return hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    } catch (e) {
      return url;
    }
  };

  // Fonction pour analyser un seul prospect
  const scanSingleProspect = async (prospect: any) => {
    try {
      const url = prospect.site_url;
      if (!url) return false;

      const normalizedUrl = normalizeUrl(url);
      const domain = extractDomain(url);
      const prospectId = prospect.id;

      await Promise.all([
        // 1. Performance de Page (PageSpeed)
        (async () => {
          try {
            await callEdge('pagespeed', {
              url: normalizedUrl,
              prospectId
            });
          } catch (err) {
          }
        })(),

        // 2. Avis Google Business
        (async () => {
          try {
            const businessName = prospect.business_name || domain;
            const location = prospect.location || '';
            await callEdge('google-reviews-fetch', {
              action: 'start',
              companyName: businessName,
              location: location,
              prospectId,
              domain,
              reviewsLimit: 5
            });
          } catch (err) {
          }
        })(),

        // 3. Analyse des mots-clés
        (async () => {
          try {
            await callEdge('dataforseo-keywords', {
              domain,
              prospectId,
              forceRefresh: true
            });
          } catch (err) {
          }
        })(),
        
        // 4. Détection CMS
        (async () => {
          try {
            const result = await whatCmsService.detectCms(normalizedUrl);

            if (prospectId && result) {
              const cmsData = {
                prospect_id: prospectId,
                url: normalizedUrl,
                detected: result.detected,
                cms_name: result.cms,
                confidence: result.confidence,
                version: result.version || null,
                logo_url: result.logo || null,
                details: result.details || null,
                technologies: result.technologies || null,
                social_profiles: result.socialProfiles || null,
              };

              await supabase
                .from('cms_detection_results')
                .upsert(cmsData, { onConflict: 'prospect_id' });
            }
          } catch (err) {
          }
        })(),

        // 5. Connectivité du site
        (async () => {
          try {
            await connectivityService.checkConnectivityWithFallback(normalizedUrl);
          } catch (err) {
          }
        })(),
        
        // 6. Intro personnalisée
        (async () => {
          try {
            if (!prospectId) {
              return;
            }

            const introData = {
              prospect_id: prospectId,
              content: `Introduction personnalisée pour ${prospect.business_name || domain}. Cette introduction sera générée automatiquement en fonction des données collectées sur votre entreprise et votre site web.`
            };

            const { error } = await supabase
              .from('personalized_intros')
              .upsert(introData, {
                onConflict: 'prospect_id',
                ignoreDuplicates: false
              });

            if (error) {
              throw error;
            }

          } catch (err) {
          }
        })()
      ]);

      const { data: prospectData } = await supabase
        .from('prospects')
        .select('metadata')
        .eq('id', prospectId)
        .single();

      const updatedMetadata = {
        ...((prospectData?.metadata as object) || {}),
        last_analyzed: new Date().toISOString(),
        analyze_status: 'completed'
      };

      await supabase
        .from('prospects')
        .update({
          metadata: updatedMetadata,
          updated_at: new Date().toISOString()
        })
        .eq('id', prospectId);

      return true;
    } catch (err) {
      return false;
    }
  };

  // Fonction pour traiter les prospects par lots
  const processProspectsBatch = async (prospects: any[]) => {
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < prospects.length; i += maxConcurrent) {
      const batch = prospects.slice(i, i + maxConcurrent);

      const results = await Promise.all(batch.map(prospect => scanSingleProspect(prospect)));

      const batchSuccessCount = results.filter(Boolean).length;
      const batchFailCount = results.length - batchSuccessCount;

      successCount += batchSuccessCount;
      failCount += batchFailCount;

      const processed = Math.min(i + batch.length, prospects.length);
      setProcessedCount(processed);
      setProgress(Math.round((processed / prospects.length) * 100));
    }

    return { successCount, failCount };
  };

  // Fonction principale pour lancer le Bulk Scan
  const handleBulkScan = async () => {
    if (isScanning) return;

    setIsScanning(true);
    setProgress(0);
    setProcessedCount(0);

    try {
      const { data: prospects, error } = await supabase
        .from('prospects')
        .select('*')
        .not('site_url', 'is', null)
        .not('site_url', 'eq', '');

      if (error) throw error;

      if (!prospects || prospects.length === 0) {
        showError('Aucun prospect', 'Aucun prospect avec URL valide trouvé pour l\'analyse en masse.');
        setIsScanning(false);
        return;
      }

      setTotalCount(prospects.length);

      const { successCount, failCount } = await processProspectsBatch(prospects);

      showSuccess(
        'Analyse en masse terminée',
        `${successCount} prospects analysés avec succès. ${failCount} échecs.`
      );

      if (onScanComplete) {
        onScanComplete();
      }
    } catch (err) {
      showError(
        'Erreur lors de l\'analyse',
        `Une erreur est survenue: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <button
      onClick={handleBulkScan}
      disabled={isScanning}
      className={`
        relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors shadow-sm
        ${isScanning 
          ? 'bg-amber-500/50 text-white cursor-not-allowed' 
          : 'bg-amber-500 text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50'
        }
      `}
      title="Lancer une analyse sur tous les prospects"
    >
      {isScanning ? (
        <>
          <Loader className="h-5 w-5 animate-spin" />
          <div className="flex flex-col items-start">
            <span>Analyse en cours ({progress}%)</span>
            <span className="text-xs">{processedCount}/{totalCount} prospects</span>
          </div>
          
          {/* Barre de progression */}
          <div className="absolute bottom-0 left-0 h-1 bg-amber-500 transition-all duration-300" style={{ width: `${progress}%` }}></div>
        </>
      ) : (
        <>
          <Scan className="h-5 w-5" />
          <span>Bulk Scan</span>
        </>
      )}
    </button>
  );
};

export default BulkScanButton;
